import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, MapPin } from 'lucide-react';
import { searchIndonesianCities } from '@lms/shared';

interface CityAutocompleteProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  dropdownLabel: string;
  emptyMessage: (query: string) => string;
  error?: string;
  errorId?: string;
}

export function CityAutocomplete({
  id,
  value,
  onChange,
  placeholder,
  dropdownLabel,
  emptyMessage,
  error,
  errorId,
}: CityAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const suggestions = searchIndonesianCities(value, 6);
  const suggestionsId = id ? `${id}-suggestions` : 'registration-city-suggestions';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex items-center rounded-xl bg-surface-container-lowest">
        <MapPin
          size={18}
          className="pointer-events-none absolute left-4 text-on-surface-variant/60"
          aria-hidden="true"
        />
        <input
          id={id}
          type="text"
          value={value}
          placeholder={placeholder}
          onFocus={() => setIsOpen(true)}
          onChange={event => {
            onChange(event.target.value);
            setIsOpen(true);
          }}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={suggestionsId}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? (errorId ?? 'registration-city-error') : undefined}
          className={`w-full rounded-xl bg-transparent py-4 pl-11 pr-12 text-sm font-body text-on-surface outline-none transition-shadow placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20 ${
            error ? 'ring-2 ring-error/40 focus:ring-error/40' : ''
          }`}
        />
        <button
          type="button"
          onClick={() => setIsOpen(open => !open)}
          aria-label={dropdownLabel}
          aria-expanded={isOpen}
          className="absolute right-3 flex size-9 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-95"
        >
          <ChevronDown
            size={17}
            className={isOpen ? 'rotate-180 transition-transform' : 'transition-transform'}
          />
        </button>
      </div>

      {isOpen && (
        <div
          id={suggestionsId}
          role="listbox"
          className="absolute z-30 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl bg-surface-container-lowest p-2 shadow-level3"
        >
          {suggestions.length ? (
            suggestions.map(city => {
              const formatted = `${city.type} ${city.name}, ${city.province}`;
              const isSelected = value === formatted;
              return (
                <button
                  key={`${city.type}-${city.name}-${city.province}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(formatted);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.99] ${
                    isSelected
                      ? 'bg-primary/10 font-bold text-primary'
                      : 'text-on-surface hover:bg-surface-container-high'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="mr-2 rounded-md bg-surface-container-high px-2 py-1 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                      {city.type}
                    </span>
                    <span>{city.name}</span>
                    <span className="ml-2 text-xs text-on-surface-variant">{city.province}</span>
                  </span>
                  {isSelected && <Check size={16} className="shrink-0" aria-hidden="true" />}
                </button>
              );
            })
          ) : (
            <p className="px-3 py-4 text-sm text-on-surface-variant">{emptyMessage(value)}</p>
          )}
        </div>
      )}
    </div>
  );
}
